import { StateEffect, StateField } from "@codemirror/state";
import { EditorView } from "@codemirror/view";

import { addEffectListener, removeEffectListener } from "./effect-listeners.js";
import { dispatchFigureModalPasteEvent, isAllowedImageType } from "./paste-image.js";
import { setMetadataEffect } from "./language.js";

export const editFigureDataEffect = StateEffect.define();

export class FigureData {
    constructor(props) {
        this.props = props;
    }

    get from() {
        return this.props.from;
    }

    get to() {
        return this.props.to;
    }

    get caption() {
        return this.props.caption;
    }

    get label() {
        return this.props.label;
    }

    get width() {
        return this.props.width;
    }

    get unknownGraphicsArguments() {
        return this.props.unknownGraphicsArguments;
    }

    get graphicsCommandArguments() {
        return this.props.graphicsCommandArguments;
    }

    get graphicsCommand() {
        return this.props.graphicsCommand;
    }

    get file() {
        return this.props.file;
    }

    map(changes) {
        return new FigureData({
            from: changes.mapPos(this.from),
            to: changes.mapPos(this.to),
            caption: mapFromTo(this.caption, changes),
            label: mapFromTo(this.label, changes),
            graphicsCommand: mapFromTo(this.graphicsCommand, changes),
            width: this.width,
            file: mapFromTo(this.file, changes),
            graphicsCommandArguments: mapFromTo(this.graphicsCommandArguments, changes),
            unknownGraphicsArguments: this.unknownGraphicsArguments,
        });
    }
}

function mapFromTo(position, changes) {
    if (!position) {
        return position;
    }

    return {
        ...position,
        from: changes.mapPos(position.from),
        to: changes.mapPos(position.to),
    };
}

function mapFigureData(current, changes) {
    if (!current) {
        return current;
    }
    return current.map(changes);
}

export const editFigureData = StateField.define({
    create() {
        return null;
    },
    update(current, transaction) {
        let nextValue;

        for (const effect of transaction.effects) {
            if (effect.is(editFigureDataEffect)) {
                nextValue = effect.value;
            }
        }

        if (nextValue !== undefined) {
            return nextValue;
        }

        return mapFigureData(current, transaction.changes);
    },
});

export function figureModal() {
    return [editFigureData];
}

export function waitForFileTreeUpdate(view) {
    const abortController = new AbortController();

    const promise = new Promise((resolve) => {
        const abort = () => {
            removeEffectListener(view, setMetadataEffect, listener);
            resolve();
        };

        function listener() {
            if (abortController.signal.aborted) {
                return;
            }
            abortController.signal.removeEventListener("abort", abort);
            resolve();
        }

        abortController.signal.addEventListener("abort", abort, { once: true });
        addEffectListener(view, setMetadataEffect, listener, { once: true });
    });

    return {
        withTimeout(afterMs = 500) {
            window.setTimeout(() => abortController.abort(), afterMs);
            return promise;
        },
        promise,
    };
}

export function figureModalPasteHandler() {
    return EditorView.domEventHandlers({
        drop(event) {
            if (!event.dataTransfer || event.dataTransfer.files.length === 0) {
                return;
            }

            const file = event.dataTransfer.files[0];
            if (!isAllowedImageType(file.type, file.name)) {
                return;
            }

            dispatchFigureModalPasteEvent({
                name: file.name,
                type: file.type,
                data: file,
            });
        },
        paste(event) {
            if (!event.clipboardData || event.clipboardData.files.length === 0) {
                return;
            }

            if (event.clipboardData.types.includes("text/plain")) {
                return;
            }

            const file = event.clipboardData.files[0];
            if (!isAllowedImageType(file.type)) {
                return;
            }

            dispatchFigureModalPasteEvent({
                name: file.name,
                type: file.type,
                data: file,
            });
        },
    });
}
